/**
 * parts.js  — parts, categories, suppliers, images, links, research
 */
import api from './auth';

/* ── Reference data ────────────────────────────────────────────────── */

export async function getStatuses() {
  const { data } = await api.get('/parts/statuses');
  return data; // { success, data: [{ refStatusId, code, name, color }] }
}

export async function getConditions() {
  const { data } = await api.get('/parts/conditions');
  return data; // { success, data: [{ refConditionId, code, name }] }
}

/* ── Categories ────────────────────────────────────────────────────── */

export async function getCategories(vehicleId) {
  const { data } = await api.get(`/vehicles/${vehicleId}/categories`);
  return data;
}

export async function generateCategories(vehicleId) {
  const { data } = await api.post(`/vehicles/${vehicleId}/ai-categories`);
  return data;
}

export async function getCategoryTree(vehicleId) {
  const { data } = await api.get('/parts/category-tree', { params: { vehicleId } });
  return data;
}

/* ── Parts list ────────────────────────────────────────────────────── */

export async function listParts(vehicleId, categoryId = null) {
  const params = { vehicleId };
  if (categoryId) params.categoryId = categoryId;
  const { data } = await api.get('/parts', { params });
  return data;
}

/** Full detail — part record + xrefs + notes (3 result sets merged) */
export async function getPartDetail(partId) {
  const { data } = await api.get(`/parts/${partId}/detail`);
  return data;
}

/** Build the absolute URL for a QR PNG stored in qr_code_path */
export function qrImageUrl(qrCodePath) {
  if (!qrCodePath) return null;
  const base = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/$/, '');
  return `${base}${qrCodePath}`;
}

/** Regenerate (or generate for the first time) the QR code for a part */
export async function regenerateQr(partId) {
  const { data } = await api.get(`/parts/${partId}/qr`);
  return data;
}

/** Search parts by name/OEM within a vehicle */
export async function searchParts(vehicleId, term) {
  const { data } = await api.get('/parts', { params: { vehicleId, search: term } });
  return data;
}

/** Merge current part (source) INTO primaryPartId (primary survives, source is deleted) */
export async function mergeIntoPart(primaryPartId, sourcePartIds) {
  const { data } = await api.post(`/parts/${primaryPartId}/consolidate`, {
    source_part_ids: sourcePartIds,
  });
  return data;
}

/** Permanently delete a part and all related data (images purged from S3) */
export async function deletePart(partId) {
  const { data } = await api.delete(`/parts/${partId}`);
  return data;
}

/** Quick-update one or more fields without touching unspecified fields */
export async function patchPart(partId, changes) {
  const { data } = await api.patch(`/parts/${partId}`, changes);
  return data;
}

/** Full upsert — supply all editable fields */
export async function savePart(partId, fields) {
  const { data } = await api.put(`/parts/${partId}`, fields);
  return data;
}

/* ── AI parts generation ───────────────────────────────────────────── */

export async function generateAiParts(vehicleId, payload) {
  const { data } = await api.post(`/vehicles/${vehicleId}/ai-parts`, payload);
  return data;
}

export async function generateSubSystemParts(vehicleId, payload) {
  const { data } = await api.post(`/vehicles/${vehicleId}/ai-parts`, payload);
  return data;
}

/* ── Images ────────────────────────────────────────────────────────── */

/** List images for a part — returns pre-signed S3 URLs */
export async function listImages(partId) {
  const { data } = await api.get(`/parts/${partId}/images`);
  return data;
}

/** Ask AI to find and save one reference image for a specific part */
export async function aiFindPartImage(partId) {
  const { data } = await api.post(`/parts/${partId}/ai-find-image`);
  return data;
}

/** Upload an image file (File object). is_primary and caption are optional. */
export async function uploadImage(partId, file, { is_primary = false, caption = '' } = {}) {
  const form = new FormData();
  form.append('image', file);
  if (is_primary) form.append('is_primary', '1');
  if (caption)    form.append('caption', caption);

  // Do NOT set Content-Type manually — the browser must set it so the
  // multipart boundary is included. Axios handles this automatically for FormData.
  const { data } = await api.post(`/parts/${partId}/images`, form);
  return data;
}

export async function deleteImage(partId, imageId) {
  const { data } = await api.delete(`/parts/${partId}/images/${imageId}`);
  return data;
}

export async function setPrimaryImage(partId, imageId) {
  const { data } = await api.put(`/parts/${partId}/images/${imageId}/primary`);
  return data;
}

/* ── Links ─────────────────────────────────────────────────────────── */

export async function getLinks(partId) {
  const { data } = await api.get(`/parts/${partId}/links`);
  return data;
}

export async function addLink(partId, url, description = '') {
  const { data } = await api.post(`/parts/${partId}/links`, { url, description });
  return data;
}

export async function deleteLink(partId, url) {
  const { data } = await api.delete(`/parts/${partId}/links`, { data: { url } });
  return data;
}

export async function starLink(partId, url, starred = true) {
  const { data } = await api.post(`/parts/${partId}/links/star`, { url, starred });
  return data;
}

/* ── Suppliers ─────────────────────────────────────────────────────── */

export async function listSuppliers(partId) {
  const { data } = await api.get(`/parts/${partId}/suppliers`);
  return data;
}

export async function addSupplier(partId, supplierData) {
  const { data } = await api.post(`/parts/${partId}/suppliers`, supplierData);
  return data;
}

export async function updateSupplier(partId, partSupplierId, supplierData) {
  const { data } = await api.put(`/parts/${partId}/suppliers/${partSupplierId}`, supplierData);
  return data;
}

export async function removeSupplier(partId, partSupplierId) {
  const { data } = await api.delete(`/parts/${partId}/suppliers/${partSupplierId}`);
  return data;
}

/** AI-powered supplier search — finds and saves listings from known vendors,
 *  eBay, and Facebook Marketplace based on part + vehicle context. */
export async function searchAiSuppliers(partId) {
  const { data } = await api.post(`/parts/${partId}/ai-supplier`);
  return data;
}

/* ── Research (AI) ─────────────────────────────────────────────────── */

export async function getResearch(partId) {
  const { data } = await api.get(`/parts/${partId}/research`);
  return data;
}

export async function generateResearch(partId) {
  const { data } = await api.post(`/parts/${partId}/research`);
  return data;
}

/** Return all parts with research data for a vehicle */
export async function getResearched(vehicleId) {
  const { data } = await api.get(`/vehicles/${vehicleId}/research`);
  return data;
}

/* ── Suppliers (global list) ────────────────────────────────────────── */

/** Return all suppliers with active_listing_count and part_count */
export async function getAllSuppliers() {
  const { data } = await api.get('/suppliers');
  return data;
}

/* ── Cart (shopping list) ───────────────────────────────────────────── */

/** Get PENDING cart items for a vehicle (includes available_suppliers JSON) */
export async function getCart(vehicleId) {
  const { data } = await api.get(`/vehicles/${vehicleId}/cart`);
  return data;
}

/** Add a part to the cart */
export async function addToCart(vehicleId, { partId, quantity = 1, priority = 'NORMAL', target_price = null, notes = null } = {}) {
  const { data } = await api.post(`/vehicles/${vehicleId}/cart`, { partId, quantity, priority, target_price, notes });
  return data;
}

/** Remove a cart item */
export async function removeFromCart(vehicleId, shoppingListId) {
  const { data } = await api.delete(`/vehicles/${vehicleId}/cart/${shoppingListId}`);
  return data;
}

/** Select or change the supplier for a cart item */
export async function pickSupplier(vehicleId, shoppingListId, { supplierId, partSupplierId } = {}) {
  const { data } = await api.put(`/vehicles/${vehicleId}/cart/${shoppingListId}/supplier`, { supplierId, partSupplierId });
  return data;
}

/** Mark a cart item as purchased — optionally record the price paid */
export async function purchaseCartItem(vehicleId, shoppingListId, { purchased_price = null } = {}) {
  const { data } = await api.post(`/vehicles/${vehicleId}/cart/${shoppingListId}/purchased`, { purchased_price });
  return data;
}

/* ── Vehicle Images ─────────────────────────────────────────────────── */

/** List all images for a vehicle — returns pre-signed S3 URLs */
export async function listVehicleImages(vehicleId) {
  const { data } = await api.get(`/vehicles/${vehicleId}/images`);
  return data;
}

/** Upload a vehicle image file */
export async function uploadVehicleImage(vehicleId, file, { is_primary = false, caption = '' } = {}) {
  const form = new FormData();
  form.append('image', file);
  if (is_primary) form.append('is_primary', '1');
  if (caption)    form.append('caption', caption);
  const { data } = await api.post(`/vehicles/${vehicleId}/images`, form);
  return data;
}

/**
 * Ask AI to find and save one reference photo for this vehicle.
 * Returns { vehicleImageId, url, caption, source_url, is_primary }
 */
export async function aiFindVehiclePhoto(vehicleId) {
  const { data } = await api.post(`/vehicles/${vehicleId}/ai-find-photo`);
  return data;
}

/** Delete a vehicle image */
export async function deleteVehicleImage(vehicleId, imageId) {
  const { data } = await api.delete(`/vehicles/${vehicleId}/images/${imageId}`);
  return data;
}

/** Set a vehicle image as primary */
export async function setPrimaryVehicleImage(vehicleId, imageId) {
  const { data } = await api.put(`/vehicles/${vehicleId}/images/${imageId}/primary`);
  return data;
}

/* ── Notes ─────────────────────────────────────────────────────────── */

export async function addNote(partId, note) {
  const { data } = await api.post(`/parts/${partId}/notes`, { note });
  return data;
}

/* ── Unknown anchor part ───────────────────────────────────────────── */

/** Get the system Unknown anchor part for a vehicle */
export async function getUnknownPart(vehicleId) {
  const { data } = await api.get(`/vehicles/${vehicleId}/unknown-part`);
  return data;
}

/** Move an image from one part to another (used to assign unknown photos) */
export async function reassignImage(fromPartId, imageId, targetPartId) {
  const { data } = await api.patch(
    `/parts/${fromPartId}/images/${imageId}/reassign`,
    { target_part_id: targetPartId }
  );
  return data;
}

/** Create a new part (POST /parts — no partId, so creates new record) */
export async function createPart(fields) {
  const { data } = await api.post('/parts', fields);
  return data;
}
